import {
  amazonListingStatusLabel,
  productStatusLabel,
  statusReasonLabel,
} from '../constants/labels.js';
import { KULLANICI_ROL_ADLARI } from '../schemas/auth.js';
import { BULK_OPERATION_LABELS } from '../schemas/bulk-operations.js';
import { formatNumber, formatPara, formatUsd } from '../utils/format-tr.js';

/**
 * İşlem Geçmişi kayıtlarının Türkçe gösterimi: ekran, ürün detayı ve Excel aynı metni üretiyor.
 *
 * Kayıtların `before`/`after` alanları ham JSON olarak basılıyordu: SA panelinde `"fiyatJpy": 12999`
 * (doğrusu SAR 129,99) görünüyordu.
 */

export interface AyrintiSatiri {
  etiket: string;
  deger: string;
}

export const ISLEM_KATEGORI_ADLARI: Record<string, string> = {
  INVENTORY: 'Envanter',
  PRICING: 'Fiyatlandırma',
  REPRICER: 'Repricer',
  SHIPPING: 'Kargo',
  DISCOVERY: 'Keşif',
  SETTINGS: 'Ayarlar',
  INTEGRATION: 'Entegrasyon',
  AUTH: 'Oturum',
  SYSTEM: 'Sistem',
};

export const ISLEM_YAPAN_ADLARI: Record<string, string> = {
  SYSTEM: 'Sistem',
  USER: 'Kullanıcı',
  WORKER: 'Arka plan görevi',
};

export const ISLEM_SONUC_ADLARI: Record<string, string> = {
  SUCCESS: 'Başarılı',
  PARTIAL: 'Kısmi',
  FAILED: 'Hatalı',
};

/** `entity_type` kolonu kod; ekranda bu adlar görünüyor. */
const KONU_ADLARI: Record<string, string> = {
  product: 'Ürün',
  amazon_listing: 'Amazon ilanı',
  bulk_operation: 'Toplu işlem',
  feed_submission: 'Amazon gönderimi',
  shipping_cost: 'Kargo ücreti',
  warehouse: 'Depo',
  product_search: 'Ürün araması',
  upload_pool: 'Yükleme havuzu',
  shipping_formula: 'Kargo formülü',
  repricer_settings: 'Repricer ayarları',
  handling_time_settings: 'Hazırlama süresi ayarları',
  handling_time: 'Hazırlama süresi',
  order: 'Sipariş kargo bildirimi',
  store: 'Envanter senkronu',
  store_reset: 'Mağaza sıfırlama',
  inventory_settings: 'Envanter ayarları',
  amazon_write_guard: 'Amazon yazma kilidi',
  notification_channel_config: 'Bildirim kanalları',
  pricing_profile: 'Fiyat profili',
  pricing_tiers: 'Kâr bantları',
  asin_rule: 'ASIN kuralı',
  amazon_fee_table: 'Amazon komisyon tablosu',
  blacklist_brand: 'Marka kısıtlaması',
  blacklist_asin: 'Ürün kısıtlaması',
  blacklist_keyword: 'Kelime kısıtlaması',
  blacklist_seller: 'Satıcı kısıtlaması',
  blacklist_category: 'Kategori kısıtlaması',
  blacklist_trusted_brand: 'Güvenilir marka',
  blacklist_trusted_product: 'Hariç tutulan ürün',
  kisitlama_taramasi: 'Kısıtlama taraması',
  envanter_cekimi: 'Envanter çekimi',
  kaynak_ulasilamiyor: 'Kaynağı alınamayan ürünler',
  bayat_koruma: 'Kaynağı doğrulanamayan ürünler',
  kargo_kurtarma: 'Kargo fiyatı gelen ürünler',
  bayat_donus: 'Kaynağı yeniden doğrulanan ürünler',
  spapi_credential: 'Amazon bağlantı bilgileri',
  aws_credential: 'AWS bilgileri',
  keepa_account: 'Keepa hesabı',
  keepa_mode: 'Kaynak modu',
  fx_rate: 'Döviz kuru',
  oturum: 'Oturum',
  sifre: 'Şifre',
  iki_adimli_dogrulama: 'İki adımlı doğrulama',
  kullanici: 'Kullanıcı',
};

/** Kayıt neyle ilgili. Bilinmeyen kod ham gösterilmiyor. */
export function islemKonusu(entityType: string | null | undefined): string {
  if (entityType === null || entityType === undefined) return '—';
  return KONU_ADLARI[entityType] ?? 'Diğer';
}

type Bicim =
  | 'para'
  | 'usd'
  | 'yuzde'
  | 'gun'
  | 'sebep'
  | 'toplu'
  | 'acikKapali'
  | 'adet'
  | 'toplam'
  | 'degisti';

const ALANLAR: Record<string, { etiket: string; bicim?: Bicim }> = {
  işlem: { etiket: 'İşlem' },
  islem: { etiket: 'İşlem' },
  type: { etiket: 'İşlem', bicim: 'toplu' },
  fiyat: { etiket: 'Fiyat', bicim: 'para' },
  fiyatJpy: { etiket: 'Fiyat', bicim: 'para' },
  secilenFiyatJpy: { etiket: 'Banda çekilmeden önceki fiyat', bicim: 'para' },
  stok: { etiket: 'Stok' },
  sku: { etiket: 'SKU' },
  sebep: { etiket: 'Sebep', bicim: 'sebep' },
  sebepler: { etiket: 'Sebepler' },
  saat: { etiket: 'Doğrulanmayan süre (saat)' },
  durum: { etiket: 'Durum' },
  costUsd: { etiket: 'Kargo ücreti', bicim: 'usd' },
  carrier: { etiket: 'Taşıyıcı' },
  teklifSayısı: { etiket: 'Teklif sayısı' },
  yonetilenSku: { etiket: 'Yönetilen SKU' },
  // Toplu işlem
  total: { etiket: 'Toplam ürün' },
  succeeded: { etiket: 'Başarılı' },
  failed: { etiket: 'Hatalı' },
  restored: { etiket: 'Geri yüklenen' },
  status: { etiket: 'Durum' },
  qty: { etiket: 'Adet' },
  handlingTimeDays: { etiket: 'Hazırlama süresi', bicim: 'gun' },
  repricerEnabled: { etiket: 'Repricer', bicim: 'acikKapali' },
  tagName: { etiket: 'Etiket' },
  minMarginPct: { etiket: 'Min kâr', bicim: 'yuzde' },
  stdMarginPct: { etiket: 'Standart kâr', bicim: 'yuzde' },
  maxMarginPct: { etiket: 'Max kâr', bicim: 'yuzde' },
  singleSellerMarginPct: { etiket: 'Tek satıcı kârı', bicim: 'yuzde' },
  deleteFromAmazon: { etiket: "Amazon'dan da silindi" },
  // Yeni listeleme
  amazonListingStatus: { etiket: 'Amazon durumu' },
  hesaptakiSkular: { etiket: 'Hesaptaki SKU' },
  // Ürün ekleme, kargo, envanter özetleri
  kaynak: { etiket: 'Kaynak' },
  eklenen: { etiket: 'Eklenen' },
  atlanan: { etiket: 'Atlanan' },
  bilerekAtlanan: { etiket: 'Atlanan' },
  varsayilanAdet: { etiket: 'Varsayılan adet' },
  varsayilanAdetle: { etiket: 'Varsayılan adetle eklenen' },
  sifirAdetle: { etiket: 'Stok 0 ile eklenen' },
  oluşturulan: { etiket: 'Oluşturulan' },
  kargoBekleyen: { etiket: 'Kargo fiyatı bekleyen' },
  yazılan: { etiket: 'Yazılan' },
  üzerineYazılan: { etiket: 'Üzerine yazılan' },
  eşitlenen: { etiket: 'Eşitlenen' },
  hatalı: { etiket: 'Hatalı' },
  toplam: { etiket: 'Toplam' },
  başarılı: { etiket: 'Başarılı' },
  güncellenen: { etiket: 'Güncellenen' },
  onayBekleyen: { etiket: 'Onay bekleyen' },
  incelenen: { etiket: 'İncelenen' },
  hazirlanan: { etiket: 'Gönderime hazırlanan' },
  adet: { etiket: 'Adet' },
  degerler: { etiket: 'Değerler' },
  ad: { etiket: 'Ad' },
  veriKaynagi: { etiket: 'Veri kaynağı' },
  // Otomatik işler
  okunan: { etiket: 'Okunan satır' },
  yeni: { etiket: 'Yeni ürün' },
  hata: { etiket: 'Hata' },
  kapatilan: { etiket: 'Kapatılan ürün' },
  acilan: { etiket: 'Yeniden açılan ürün' },
  kaldirmayaAlinan: { etiket: "Amazon'dan kaldırılacak ürün" },
  silmesiBaslatilan: { etiket: 'Silinmeye başlanan ürün' },
  kayit: { etiket: 'Kayıt' },
  kabul: { etiket: 'Kabul edilen' },
  red: { etiket: 'Reddedilen' },
  feedId: { etiket: 'Gönderim numarası' },
  kur: { etiket: 'Kur' },
  kaynakModu: { etiket: 'Kaynak modu' },
  // Sipariş kargo bildirimi
  amazonOrderId: { etiket: 'Sipariş numarası' },
  tasiyici: { etiket: 'Taşıyıcı' },
  takipNo: { etiket: 'Takip numarası' },
  duzeltme: { etiket: 'Düzeltme gönderimi' },
  // Kullanıcı yönetimi
  email: { etiket: 'E-posta' },
  rol: { etiket: 'Rol' },
  aktif: { etiket: 'Etkin' },
  // Mağaza sıfırlama, depo
  name: { etiket: 'Ad' },
  label: { etiket: 'Ad' },
  sellerId: { etiket: 'Satıcı kimliği' },
  silinen: { etiket: 'Silinen kayıt', bicim: 'toplam' },
  baseUrl: { etiket: 'Adres' },
  selectorProfileUpdated: { etiket: 'Seçici profili güncellendi' },
  // Envanter ayarları
  excludeTrademark: { etiket: 'Ticari markalı ürünleri dışla' },
  excludeSellerBranded: { etiket: 'Satıcı markalı ürünleri dışla' },
  excludeBlacklistedBrand: { etiket: 'Yasaklı markalı ürünleri dışla' },
  excludeBlacklistedKeyword: { etiket: 'Yasaklı kelimeli ürünleri dışla' },
  excludeSba: { etiket: 'SBA ürünleri dışla' },
  excludeFbm: { etiket: 'Kaynakta FBM ürünleri dışla' },
  excludeDigital: { etiket: 'Dijital ürünleri dışla' },
  excludeChineseSeller: { etiket: 'Kaynakta Çinli satıcıyı dışla' },
  jpSellerCountMin: { etiket: 'Satıcı sayısı — en az' },
  jpSellerCountMax: { etiket: 'Satıcı sayısı — en fazla' },
  jpExcludeIfPrime: { etiket: 'Satış pazarında Prime satıcı varsa' },
  jpExcludeIfAmazon: { etiket: 'Satış pazarında satıcı Amazon ise' },
  jpExcludeIfFba: { etiket: 'Satış pazarında FBA satıcı varsa' },
  jpExcludeIfSameSeller: { etiket: 'Aynı satıcı varsa' },
  jpSameSellerMatch: { etiket: 'Aynı satıcı eşleşme sıkılığı' },
  jpExcludeIfChinese: { etiket: 'Satış pazarında Çinli satıcı varsa' },
  jpExcludeIfBlacklistedSeller: { etiket: 'Yasaklı satıcı listem' },
  useRegionAverageShipping: { etiket: 'Kargoda bölge ortalaması' },
  includeDiscountedPrice: { etiket: 'İndirimli fiyatı dahil et' },
  includePrimeDiscountedPrice: { etiket: 'Prime indirimli fiyatı dahil et' },
  includeExtraTax10: { etiket: 'Ek vergi' },
  isTaxRegisteredInSaleCountry: { etiket: 'Satış ülkesinde vergi mükellefi' },
  deleteWhenRemovedFromAmazon: { etiket: 'Amazon’da satıştan kaldırılanlar' },
  deleteOnFairPricingWarning: { etiket: 'Fair Pricing uyarısı alanlar' },
  deleteOnRestrictedProductsRemoval: { etiket: 'Restricted Products bildirimi alanlar' },
  deleteOnProblemProductNotice: { etiket: 'Sorunlu ürün bildirimi alanlar' },
  poolAddBrandFromMine: { etiket: 'Kendi markalarımı ortak havuza ekle' },
  poolAddBrandFromPool: { etiket: 'Ortak havuzdaki markaları ekle' },
  poolAddProductFromMine: { etiket: 'Kendi ürünlerimi ortak havuza ekle' },
  poolAddProductFromPool: { etiket: 'Ortak havuzdaki ürünleri ekle' },
  maxStalenessHours: { etiket: 'Azami kontrolsüz kalma (saat)' },
  unverifiedSourceActionHours: { etiket: 'Kaynak doğrulanamazsa müdahale (saat)' },
  unverifiedSourceAction: { etiket: 'Doğrulanamayınca yapılacak' },
  sourceErrorCountThreshold: { etiket: 'Hata eşiği' },
  kuralEylemleri: { etiket: 'Kural eylemleri', bicim: 'degisti' },
  fiyatSaatKisiti: { etiket: 'Fiyatları yalnız belirli saatlerde güncelle' },
  fiyatSaatBaslangic: { etiket: 'Fiyat saati başlangıç' },
  fiyatSaatBitis: { etiket: 'Fiyat saati bitiş' },
  fiyatSaatArtisSerbest: { etiket: 'Fiyat artışları her saatte' },
  skuPrefix: { etiket: 'SKU öneki' },
  skuTemplate: { etiket: 'SKU şablonu' },
  // Fiyat profili ve ASIN kuralı
  isDefault: { etiket: 'Varsayılan' },
  marginBasis: { etiket: 'Kâr hesabı tabanı' },
  includeAmazonFeeTax: { etiket: 'Komisyon vergisi' },
  amazonFeeTaxPct: { etiket: 'Komisyon vergisi', bicim: 'yuzde' },
  isTaxRegistered: { etiket: 'Vergi mükellefi' },
  salesTaxPct: { etiket: 'Satış vergisi', bicim: 'yuzde' },
  extraTaxPct: { etiket: 'Ek vergi', bicim: 'yuzde' },
  purchaseTaxPct: { etiket: 'Alış vergisi', bicim: 'yuzde' },
  commissionMode: { etiket: 'Komisyon hesabı' },
  fixedCommissionPct: { etiket: 'Sabit komisyon', bicim: 'yuzde' },
  fxMode: { etiket: 'Kur kaynağı' },
  fxManualRate: { etiket: 'Elle girilen kur' },
  fxSafetyMarginPct: { etiket: 'Kur güvenlik marjı', bicim: 'yuzde' },
  extraCostUsd: { etiket: 'Ek maliyet', bicim: 'usd' },
  priceRounding: { etiket: 'Fiyat yuvarlama' },
  listingPriceMode: { etiket: 'Listeleme fiyatı' },
  feePct: { etiket: 'Komisyon oranı', bicim: 'yuzde' },
  categoryName: { etiket: 'Kategori' },
  minPriceJpy: { etiket: 'En düşük fiyat', bicim: 'para' },
  maxPriceJpy: { etiket: 'En yüksek fiyat', bicim: 'para' },
  fixedPriceJpy: { etiket: 'Sabit fiyat', bicim: 'para' },
  shippingCostUsdOverride: { etiket: 'Kargo ücreti', bicim: 'usd' },
  referralFeePctOverride: { etiket: 'Komisyon oranı', bicim: 'yuzde' },
  repricerDisabled: { etiket: 'Repricer kapalı' },
  isActive: { etiket: 'Etkin' },
  note: { etiket: 'Not' },
  asinSayisi: { etiket: 'ASIN sayısı' },
  silinenKural: { etiket: 'Silinen kural' },
  dosya: { etiket: 'Dosya' },
  degismeyen: { etiket: 'Değişmeyen' },
  ornek: { etiket: 'Örnek ASIN' },
  // Repricer ayarları
  isEnabled: { etiket: 'Repricer', bicim: 'acikKapali' },
  açık: { etiket: 'Repricer', bicim: 'acikKapali' },
  mode: { etiket: 'Çalışma modu' },
  target: { etiket: 'Hedef' },
  fallbackTarget: { etiket: 'Yedek hedef' },
  strategy: { etiket: 'Strateji' },
  amountType: { etiket: 'Fark türü' },
  amount: { etiket: 'Fark' },
  intervalMinutes: { etiket: 'Tarama aralığı (dakika)' },
  minChangeJpy: { etiket: 'En az değişim', bicim: 'para' },
  minChangePct: { etiket: 'En az değişim', bicim: 'yuzde' },
  cooldownMinutes: { etiket: 'Bekleme süresi (dakika)' },
  maxChangesPerDay: { etiket: 'Ürün başına günlük azami değişim' },
  excludeAmazon: { etiket: 'Amazon’u rakip sayma' },
  excludeFba: { etiket: 'FBA filtresi' },
  excludePrime: { etiket: 'Prime filtresi' },
  excludeChinese: { etiket: "Çin'den gönderen teklifleri rakip sayma" },
  excludeBlacklistedSellers: { etiket: 'Yasaklı satıcıyı rakip sayma' },
  minSellerRating: { etiket: 'En düşük satıcı puanı' },
  ignoreOffersBelowPct: { etiket: 'Hatalı teklif eşiği', bicim: 'yuzde' },
  noCompetitorAction: { etiket: 'Rakip yoksa' },
  belowMinAction: { etiket: 'Rakip en düşük fiyatın altına inerse' },
  fairPricingMultiplier: { etiket: 'Fiyat politikası katsayısı' },
  amazonAutomatePricingDisabledAck: { etiket: 'Amazon otomatik fiyatlandırması kapatıldı onayı' },
  notifySkipSingleSeller: { etiket: 'Tek satıcılı ürünlerin bildirimini atla' },
  notifyMinReviewCount: { etiket: 'En az yorum sayısı' },
  notifyRequireSalesRank: { etiket: 'Satış sırası zorunlu' },
  notifyMaxSalesRank: { etiket: 'En kötü satış sırası' },
  // Amazon yazma kilidi
  writeEnabled: { etiket: 'Amazon’a yazma', bicim: 'acikKapali' },
  shipmentConfirmEnabled: { etiket: 'Kargo bildirimi', bicim: 'acikKapali' },
  otherSoftwareDisabledAck: { etiket: 'Diğer yazılım kapatıldı onayı' },
  otherSoftwareName: { etiket: 'Diğer yazılım' },
  allowedSkus: { etiket: 'İzinli SKU sayısı', bicim: 'adet' },
  maxWritesPerHour: { etiket: 'Saatlik yazma sınırı' },
  rampScope: { etiket: 'Kademeli açılış kapsamı' },
  rampEnabled: { etiket: 'Kademeli açılış', bicim: 'acikKapali' },
  rampInitialWrites: { etiket: 'İlk gün yazma sayısı' },
  rampDailyMultiplier: { etiket: 'Günlük artış katı' },
  maxWritesPerDay: { etiket: 'Günlük yazma sınırı' },
  // Hazırlama süresi ayarları
  warehouseProcessingDays: { etiket: 'Depo işlem süresi', bicim: 'gun' },
  bufferDays: { etiket: 'Güvenlik payı', bicim: 'gun' },
  defaultSupplierDays: { etiket: 'Varsayılan tedarik süresi', bicim: 'gun' },
  minHandlingDays: { etiket: 'En kısa hazırlama süresi', bicim: 'gun' },
  maxHandlingDays: { etiket: 'En uzun hazırlama süresi', bicim: 'gun' },
  weekendAware: { etiket: 'Hafta sonunu hesaba kat' },
  useScrapedDeliveryEstimate: { etiket: 'Amazon teslimat tahminini kullan' },
  roundUpToBusinessDays: { etiket: 'İş gününe yuvarla' },
  maxIncreasePerPushDays: { etiket: 'Tek seferde en fazla artış', bicim: 'gun' },
  // Bildirim kanalları
  emailEnabled: { etiket: 'E-posta bildirimi', bicim: 'acikKapali' },
  emailTo: { etiket: 'E-posta adresi' },
  telegramEnabled: { etiket: 'Telegram bildirimi', bicim: 'acikKapali' },
  telegramChatId: { etiket: 'Telegram sohbeti' },
  // Kargo formülü
  unitType: { etiket: 'Birim' },
  baseFeeUsd: { etiket: 'Taban ücret', bicim: 'usd' },
  includedUnits: { etiket: 'Dahil birim' },
  perUnitFeeUsd: { etiket: 'Birim ücreti', bicim: 'usd' },
  minBillableUnits: { etiket: 'En az ücretlendirilen birim' },
  unitRounding: { etiket: 'Birim yuvarlama' },
  useDimensionalWeight: { etiket: 'Hacimsel ağırlık' },
  dimDivisorIn3PerLb: { etiket: 'Hacim böleni' },
  desiDivisorCm3: { etiket: 'Desi böleni' },
  fallbackWeightLbs: { etiket: 'Ölçü yoksa varsayılan ağırlık' },
  fallbackAction: { etiket: 'Ölçü yoksa yapılacak' },
  minTotalUsd: { etiket: 'En düşük toplam', bicim: 'usd' },
  maxTotalUsd: { etiket: 'En yüksek toplam', bicim: 'usd' },
  surchargeUsd: { etiket: 'Ek ücret', bicim: 'usd' },
  surchargePct: { etiket: 'Ek ücret', bicim: 'yuzde' },
  bands: { etiket: 'Ücret bandı sayısı', bicim: 'adet' },
  // Kâr bandı
  costFromUsd: { etiket: 'Maliyet başlangıcı', bicim: 'usd' },
  costToUsd: { etiket: 'Maliyet bitişi', bicim: 'usd' },
  minQty: { etiket: 'En az adet' },
  defaultQty: { etiket: 'Varsayılan adet' },
};

/** Gösterilmeyen teknik alanlar: ekranda karşılığı yok ya da başlıktan zaten belli. */
const GOSTERILMEYEN = new Set([
  'id',
  'storeId',
  'createdAt',
  'updatedAt',
  'createdBy',
  'updatedBy',
  'asin',
  'tagId',
  'qtyMode',
  'blacklistKind',
  'fiyatKaynagi',
  'kirpma',
  'amazonDurumlari',
  'skuyuKullananUrunId',
  'skuyuKullananSilinmis',
  'amazonReddi',
  'adim',
  'warehouseId',
  'packageReferenceId',
  'enabledAt',
  'enabledBy',
  'disabledAt',
  'disabledBy',
  'rampStartedAt',
  'pricingProfileId',
  'sortOrder',
  'externalPriceChangeCount3d',
  'autoPausedAt',
  'autoPausedReason',
]);

/** Kod olarak yazılan, `statusReasonLabel`da karşılığı olmayan sebepler. */
const SEBEP_KODLARI: Record<string, string> = {
  ASIN_RULE: 'ASIN kuralı',
  PRICING_ERROR: 'Fiyat hesaplanamıyor',
  MEVCUT_ILANA_BAGLANDI: 'Hesaptaki mevcut ilana bağlandı',
  HESAPTA_BASKA_SKU_ILE_ACIK: 'Hesapta başka SKU ile açık ilan var',
  RECONCILE: 'Gönderilmemiş değişiklik yeniden gönderildi',
  MARGIN_VIOLATION: 'Fiyat kâr sınırının dışında',
  FAIR_PRICING: 'Fiyat politikası sınırı',
  REPRICE: 'Repricer kararı',
};

/** Alan değeri kod olan ayarların Türkçesi. */
const DEGER_ADLARI: Record<string, Record<string, string>> = {
  rol: KULLANICI_ROL_ADLARI,
  kaynak: {
    'dogrudan-ekleme': 'Doğrudan ekleme',
    'upload-pool': 'Yükleme havuzu',
    dosya: 'Excel ya da CSV dosyası',
  },
  kaynakModu: {
    KEEPA_ONLY: 'Yalnız Keepa',
    SCRAPER_ONLY: 'Yalnız kendi taramamız',
    BOTH: 'Keepa ve kendi taramamız',
    MANUAL: 'Manuel tarama',
  },
  veriKaynagi: { KEEPA: 'Keepa', MANUEL: 'Manuel tarama', MANUEL_PC: 'Manuel tarama (bilgisayar)' },
  mode: { OFF: 'Kapalı', NOTIFICATION: 'Bildirim tetikli', SCHEDULED: 'Planlı', HYBRID: 'Hibrit' },
  target: { BUYBOX: 'BuyBox', LOWEST: 'En düşük fiyat' },
  fallbackTarget: { LOWEST: 'En düşük fiyat', NONE: 'Yok' },
  strategy: { BELOW: 'Rakibin altında', ABOVE: 'Rakibin üstünde', MATCH: 'Rakiple aynı' },
  amountType: { FIXED: 'Sabit tutar', PERCENT: 'Yüzde' },
  noCompetitorAction: {
    SINGLE_SELLER_PRICE: 'Tek satıcı fiyatını uygula',
    MAX_PRICE: 'Azami fiyatı uygula',
    STD_PRICE: 'Standart fiyatı uygula',
    KEEP: 'Fiyata dokunma',
  },
  belowMinAction: {
    STAY_AT_MIN: 'En düşük fiyatta kal',
    GO_INACTIVE: 'Ürünü pasife al',
    GO_TO_MAX: 'Azami fiyata çık',
    KEEP: 'Fiyata dokunma',
  },
  marginBasis: { COST: 'Maliyet üzerinden', SALE_PRICE: 'Satış fiyatı üzerinden' },
  commissionMode: { FIXED: 'Sabit', AMAZON: 'Amazon oranı', MAX_OF_BOTH: 'İkisinden yüksek olan' },
  fxMode: { AUTO: 'Otomatik', MANUAL: 'Elle' },
  priceRounding: {
    NONE: 'Yok',
    CEIL_10: "10'a yukarı",
    CEIL_100: "100'e yukarı",
    PSYCH_80: '80 ile biten',
    PSYCH_99: '99 ile biten',
  },
  listingPriceMode: {
    NORMAL: 'Standart',
    MIN: 'En düşük',
    BUYBOX: 'BuyBox',
    LOWEST: 'En düşük rakip',
    BUYBOX_ELSE_LOWEST: 'BuyBox, yoksa en düşük rakip',
  },
  jpSameSellerMatch: {
    EXACT: 'Tam eşleşme',
    SIMILAR_90: '%90 benzer',
    SIMILAR_75: '%75 benzer',
    SIMILAR_50: '%50 benzer',
  },
  unverifiedSourceAction: { SET_QTY_ZERO: 'Stoğu sıfırla', GO_INACTIVE: 'Pasife al' },
  rampScope: { NEW_LISTINGS_ONLY: 'Yalnız yeni ilanlar', ALL: 'Tüm yazmalar' },
  unitType: { LBS: 'Libre', DESI: 'Desi', KG: 'Kilogram' },
  unitRounding: { NONE: 'Yok', CEIL_0_5: "0,5'e yukarı", CEIL_1: "1'e yukarı" },
  fallbackAction: {
    USE_FALLBACK: 'Varsayılan ağırlığı kullan',
    BLOCK_PRODUCT: 'Ürünü beklet',
    USE_DIM_ONLY: 'Yalnız hacmi kullan',
  },
};

const SILME_KURALI: Record<string, string> = {
  NOTHING: 'Hiçbir şey yapma',
  REMOVE_FROM_INVENTORY: 'Envanterden sil',
  REMOVE_AND_BLACKLIST: 'Envanterden sil ve yasakla',
};
for (const alan of [
  'deleteWhenRemovedFromAmazon',
  'deleteOnFairPricingWarning',
  'deleteOnRestrictedProductsRemoval',
  'deleteOnProblemProductNotice',
]) {
  DEGER_ADLARI[alan] = SILME_KURALI;
}

const yaziDizisi = (dizi: unknown[]): string =>
  dizi
    .map((oge) =>
      oge !== null && typeof oge === 'object' && 'sku' in oge ? String(oge.sku) : String(oge),
    )
    .join(', ');

function metneCevir(alan: string, bicim: Bicim | undefined, deger: unknown): string {
  if (bicim === 'degisti') return 'değişti';
  if (bicim === 'adet' && Array.isArray(deger)) return formatNumber(deger.length);
  if (bicim === 'toplam' && deger !== null && typeof deger === 'object') {
    const toplam = Object.values(deger).reduce<number>((t, n) => t + Number(n || 0), 0);
    return formatNumber(toplam);
  }
  if (typeof deger === 'number' || typeof deger === 'string') {
    const sayi = Number(deger);
    if (bicim === 'para' && Number.isFinite(sayi)) return formatPara(sayi);
    if (bicim === 'usd' && Number.isFinite(sayi)) return formatUsd(sayi);
    if (bicim === 'yuzde' && Number.isFinite(sayi)) return `%${formatNumber(sayi)}`;
    if (bicim === 'gun') return `${String(deger)} gün`;
  }
  if (typeof deger === 'string') {
    if (bicim === 'sebep') return SEBEP_KODLARI[deger] ?? statusReasonLabel(deger);
    if (bicim === 'toplu') {
      return (BULK_OPERATION_LABELS as Record<string, string>)[deger] ?? 'Toplu işlem';
    }
    if (alan === 'status') return productStatusLabel(deger);
    if (alan === 'amazonListingStatus') return amazonListingStatusLabel(deger);
    return DEGER_ADLARI[alan]?.[deger] ?? deger;
  }
  if (typeof deger === 'number') return formatNumber(deger);
  if (typeof deger === 'boolean') {
    if (bicim === 'acikKapali') return deger ? 'açık' : 'kapalı';
    return deger ? 'Evet' : 'Hayır';
  }
  if (Array.isArray(deger)) return yaziDizisi(deger);
  if (deger !== null && typeof deger === 'object') {
    return Object.entries(deger)
      .map(([k, v]) => `${ALANLAR[k]?.etiket ?? k}: ${metneCevir(k, ALANLAR[k]?.bicim, v)}`)
      .join(' · ');
  }
  return String(deger);
}

/** Toplu işlem kayıtları parametreleri `params` içinde taşıyor; okunurken düzleşiyor. */
function nesne(deger: unknown): Record<string, unknown> {
  if (deger === null || typeof deger !== 'object' || Array.isArray(deger)) return {};
  const { params, ...kalan } = deger as Record<string, unknown>;
  return params !== null && typeof params === 'object' && !Array.isArray(params)
    ? { ...kalan, ...(params as Record<string, unknown>) }
    : kalan;
}

const birSey = (deger: unknown): boolean => deger !== null && deger !== undefined;

/**
 * Önceki ve sonraki değeri okunur satırlara çevirir: "Fiyat: £12,99 → £11,99".
 *
 * Ayar kayıtları satırın tamamını taşıyor (40+ alan); yalnız değişenler gösteriliyor.
 * Etiketi olmayan alan kod olarak basılmıyor, sayısı tek satırda veriliyor.
 */
export function islemAyrintisi(once: unknown, sonra: unknown): AyrintiSatiri[] {
  // Kâr bantları ve otomatik süre uzatması kaydı dizi taşıyor
  if (Array.isArray(once) || Array.isArray(sonra)) {
    const adet = (d: unknown) => (Array.isArray(d) ? d.length : null);
    const eski = adet(once);
    const yeni = adet(sonra);
    const degisti = JSON.stringify(once) !== JSON.stringify(sonra);
    return [
      {
        etiket: 'Kayıt sayısı',
        deger:
          eski !== null && yeni !== null && eski !== yeni
            ? `${formatNumber(eski)} → ${formatNumber(yeni)}`
            : formatNumber(yeni ?? eski ?? 0),
      },
      ...(degisti && eski !== null && yeni !== null
        ? [{ etiket: 'Değerler', deger: 'değişti' }]
        : []),
    ];
  }

  const oncesi = nesne(once);
  const sonrasi = nesne(sonra);
  const satirlar: AyrintiSatiri[] = [];
  let etiketsiz = 0;

  for (const alan of new Set([...Object.keys(oncesi), ...Object.keys(sonrasi)])) {
    if (GOSTERILMEYEN.has(alan)) continue;
    const eski = oncesi[alan];
    const yeni = sonrasi[alan];
    if (!birSey(eski) && !birSey(yeni)) continue;
    if (alan in oncesi && alan in sonrasi && JSON.stringify(eski) === JSON.stringify(yeni)) {
      continue;
    }

    const tanim = ALANLAR[alan];
    if (tanim === undefined) {
      etiketsiz++;
      continue;
    }
    const yaz = (deger: unknown) => metneCevir(alan, tanim.bicim, deger);
    const ikiyuzlu = alan in oncesi && alan in sonrasi;
    satirlar.push({
      etiket: tanim.etiket,
      deger: ikiyuzlu
        ? `${birSey(eski) ? yaz(eski) : '—'} → ${birSey(yeni) ? yaz(yeni) : '—'}`
        : yaz(birSey(yeni) ? yeni : eski),
    });
  }

  if (etiketsiz > 0) satirlar.push({ etiket: 'Diğer alanlar', deger: `${etiketsiz} değişiklik` });
  return satirlar;
}

/** Aynı `action` farklı işlere karşılık geliyor; başlık işi söylesin. */
export function islemBasligi(item: {
  action: string;
  category?: string | null;
  entityType?: string | null;
  result?: string | null;
}): string {
  if (item.entityType === 'amazon_listing') {
    if (item.action === 'CREATE') return "Amazon'da ilan açıldı";
    if (item.action === 'DELETE') return "Amazon'dan ilan kaldırıldı";
  }
  if (item.entityType === 'feed_submission') return "Amazon'a toplu gönderim";
  if (item.entityType === 'upload_pool') return 'Ürünler mağazaya aktarıldı';
  if (item.entityType === 'envanter_cekimi') return "Envanter Amazon'dan çekildi";
  if (item.entityType === 'bulk_operation') {
    if (item.action === 'ROLLBACK') return 'Toplu işlem geri alındı';
    if (item.action === 'DELETE') return 'Toplu silme';
    if (item.action === 'UPDATE') return 'Toplu işlem';
  }
  if (item.action === 'PUSH') {
    const ad = item.category === 'PRICING' ? 'Fiyat' : 'Stok';
    return item.result === 'FAILED' ? `${ad} Amazon'a gidemedi` : `${ad} Amazon'a gönderildi`;
  }
  if (item.action === 'UPDATE') {
    if (item.category === 'REPRICER') return 'Repricer fiyat kararı';
    if (item.category === 'PRICING' && item.entityType !== 'fx_rate') return 'Fiyat düzeltildi';
    if (item.category === 'INTEGRATION' && (item.entityType ?? 'product') === 'product') {
      return 'Kaynak verisi değişti';
    }
  }
  const urunKaydi = (item.entityType ?? 'product') === 'product';
  return (
    (urunKaydi ? URUN_ISLEM_ADLARI[item.action] : undefined) ?? ISLEM_ADLARI[item.action] ?? 'İşlem'
  );
}

const URUN_ISLEM_ADLARI: Record<string, string> = {
  CREATE: 'Ürün eklendi',
  ACTIVATE: 'Aktif yapıldı',
  DEACTIVATE: 'Pasif yapıldı',
};

/** `ActivityAction` enum'unun tamamı: kullanıcı ham değer görmemeli (§19). */
const ISLEM_ADLARI: Record<string, string> = {
  CREATE: 'Eklendi',
  UPDATE: 'Güncellendi',
  DELETE: 'Silindi',
  PUSH: "Amazon'a gönderildi",
  SYNC: 'Eşitlendi',
  IMPORT: 'İçeri alındı',
  EXPORT: 'Dışa aktarıldı',
  LOGIN: 'Giriş yapıldı',
  LOGOUT: 'Çıkış yapıldı',
  LOGIN_FAILED: 'Giriş denemesi başarısız',
  PAUSE: 'Duraklatıldı',
  RESUME: 'Sürdürüldü',
  ROLLBACK: 'Geri alındı',
  ACTIVATE: 'Açıldı',
  DEACTIVATE: 'Kapatıldı',
  RECALCULATE: 'Yeniden hesaplandı',
  QUOTE: 'Kargo soruldu',
  PUBLISH: 'Yayımlandı',
};

/** Milisaniye yerine okunur süre: "850 ms" değil "1 sn", "3 dk 20 sn". */
export function islemSuresi(ms: number): string {
  const saniye = Math.max(1, Math.round(ms / 1000));
  if (saniye < 60) return `${saniye} sn`;
  const dakika = Math.floor(saniye / 60);
  if (dakika < 60) return saniye % 60 === 0 ? `${dakika} dk` : `${dakika} dk ${saniye % 60} sn`;
  const saat = Math.floor(dakika / 60);
  return dakika % 60 === 0 ? `${saat} sa` : `${saat} sa ${dakika % 60} dk`;
}
